export interface EarlyBirdDto {
  /** This event's own cutoff override, or null if it's using the platform default. */
  earlyBirdUntil: string | null;
  platformDefault: string;
  /** The date actually in effect — `earlyBirdUntil` if set, otherwise `platformDefault`. */
  effectiveUntil: string;
  /** Master on/off switch — when false, every ticket prices at regular regardless of the cutoff date. */
  enabled: boolean;
  /**
   * Display only: whether the landing header prints the cutoff date beside the Early Bird badge.
   * Independent of `enabled` on purpose — hiding the date must not reprice a single ticket.
   */
  dateVisible: boolean;
}

/** TICK-090 — when this event's buyers see their ticket, and what goes out afterwards. */
export type TicketDeliveryMode = 'verified_channel' | 'instant' | 'instant_and_send';

export interface TicketDeliveryDto {
  mode: TicketDeliveryMode;
  /** Only read by the server in `instant_and_send`, but kept across mode changes. */
  copyChannels: DeliveryChannel[];
  /**
   * Whether each channel could actually send right now. Separate from `copyChannels` because the
   * provider switches are platform-level: a channel an owner may tick here is not necessarily a
   * channel that will deliver, and the form has to be able to say so.
   */
  channelAvailability: Record<DeliveryChannel, boolean>;
  /** Platform-wide and read-only from this screen — reported so the copy can tell the truth. */
  myTicketPortalEnabled: boolean;
}

export interface CheckoutSettingsDto {
  /** This event's own Checkout hold-time override in minutes, or null if it's using the platform default. */
  reservationMinutes: number | null;
  platformDefault: number;
  /** The minute value actually in effect — `reservationMinutes` if set, otherwise `platformDefault`. */
  effectiveMinutes: number;
}

export interface TicketCustomFieldDto {
  key: string;
  labelEn: string;
  labelTh: string;
  type: 'text' | 'number' | 'select';
  required: boolean;
  options?: string[];
}

export interface TicketDto {
  id: string;
  en: string;
  th: string;
  price: number;
  priceRegular: number;
  seats: number;
  capacity: number;
  remaining: number;
  soldOut: boolean;
  promotion: boolean;
  maxPerOrder: number;
  customFields: TicketCustomFieldDto[];
}

export interface EventFaqSection {
  id: string;
  type: 'faq';
  title: string;
  items: { q: string; a: string }[];
}
export interface EventSponsorsSection {
  id: string;
  type: 'sponsors';
  title: string;
  items: { name: string; url: string; logoUrl: string | null }[];
}
export type EventSection = EventFaqSection | EventSponsorsSection;

export interface EventDto {
  name: string;
  tagline: string;
  earlyBirdNote: string;
  heroImageUrl: string | null;
  /**
   * The phone-shaped crop of the hero, when this event uploaded one. Null means there is only the
   * landscape image, which every viewport then gets — see `HeroMedia` in `Landing`.
   */
  heroImagePortraitUrl: string | null;
  facts: { k: string; v: string }[];
  lineupDays: number;
  lineup: { name: string; time: string; photoUrl: string | null }[];
  sections: EventSection[];
  contactEmail: string;
  company: string;
  phase: 'early-bird' | 'regular';
  phaseLabel: string;
  ticketsTagline: string;
  ticketsTaglineVisible: boolean;
  earlyBirdUntil: string;
  /** Whether the Early Bird badge shows this date to customers. Pricing ignores it entirely. */
  earlyBirdDateVisible: boolean;
  holdMinutes: number;
  /** Derived from the event's default payment account — null when none is configured. */
  bank: { bankName: string; accountName: string; accountNo: string; qrImageUrl: string | null } | null;
  consentVersion: string;
  /**
   * TICK-096 — the organizer's own LINE OA, for the "add friend" CTA on the order-status page.
   * Marketing only: it never carries a ticket, and a customer who ignores it loses nothing. Null
   * when this event configured no OA, or disabled the one it had.
   */
  organizerOa: { addFriendUrl: string; message: string } | null;
  tickets: TicketDto[];
}

export interface ContentFaqSection {
  id: string;
  type: 'faq';
  title: string;
  items: { q: string; a: string }[];
}
export interface ContentSponsorsSection {
  id: string;
  type: 'sponsors';
  title: string;
  items: { name: string; url: string; logoKey: string | null }[];
}
export type ContentSection = ContentFaqSection | ContentSponsorsSection;

export interface ContentDto {
  name: string;
  tagline: string;
  earlyBirdNote: string;
  ticketsTagline: string;
  ticketsTaglineVisible: boolean;
  contactEmail: string;
  company: string;
  heroImageKey: string | null;
  /** Optional portrait crop for phones. Null = serve the landscape hero everywhere. */
  heroImagePortraitKey: string | null;
  facts: { k: string; v: string }[];
  lineupDays: number;
  lineup: { name: string; time: string; photoKey: string | null }[];
  sections: ContentSection[];
  comingSoon: ComingSoonDto;
}

/** TICK-081 — the copy shown at the event's own URL while it is hidden. Edited in Admin › Landing Page. */
export interface ComingSoonDto {
  headline: string;
  eyebrow: string;
  message: string;
  ctaLabel: string;
  ctaUrl: string;
  showHero: boolean;
}

/**
 * What `GET /api/e/:slug/teaser` answers. Deliberately not an `EventDto`: it carries no tickets,
 * no prices and no lineup, so nothing can hand it to code that expects an event on sale.
 */
export interface ComingSoonStateDto {
  enabled: boolean;
  salesMode: EventSalesMode;
}

export interface PreviewPassDto {
  token: string;
  expiresAt: string;
  /** The window the organizer picked, echoed back so the UI never has to infer it from the clock. */
  minutes: number;
}

export interface EventTeaserDto {
  name: string;
  headline: string;
  eyebrow: string;
  message: string;
  dateLabel: string;
  ctaLabel: string;
  ctaUrl: string;
  heroImageUrl: string | null;
  heroImagePortraitUrl: string | null;
}

export const WRISTBAND_COLORS = ['red', 'blue', 'green', 'yellow', 'orange', 'purple', 'white'] as const;
export type WristbandColor = (typeof WRISTBAND_COLORS)[number];

export interface WristbandDto {
  id: string;
  name: string;
  color: WristbandColor;
  createdAt: string;
}

export const PAYMENT_PROVIDERS = ['promptpay', 'kshop', 'mae_manee', 'tungngern', 'bank'] as const;
export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number];

export interface PaymentAccountDto {
  id: string;
  provider: PaymentProvider;
  identifier: string;
  promptpayType: 'msisdn' | 'natid' | 'ewallet' | null;
  merchantName: string | null;
  displayName: string;
  bankName: string | null;
  /** TICK-040 S3 — BOT 3-digit code. Null means a masked receiver never auto-verifies for this account. */
  bankCode: string | null;
  qrImageKey: string | null;
  qrImageUrl: string | null;
  /** TICK-049: optional BILLERID for merchant-QR accounts — enables the slip receiver check. */
  receiverProxyId: string | null;
  isDefault: boolean;
  enabled: boolean;
  createdAt: string;
}

/** The account that produced a given order's QR — may differ from the default after a fallback. */
export interface OrderPaymentAccountDto {
  provider: PaymentProvider;
  displayName: string;
  identifier: string;
  bankName: string | null;
  bankCode: string | null;
  qrImageUrl: string | null;
}

export interface PaymentAccountInput {
  provider: PaymentProvider;
  identifier: string;
  promptpayType?: 'msisdn' | 'natid' | 'ewallet' | null;
  merchantName?: string | null;
  displayName: string;
  bankName?: string | null;
  bankCode?: string | null;
  qrImageKey?: string | null;
  receiverProxyId?: string | null;
  isDefault?: boolean;
}

export interface WristbandBinding {
  wristbandId: string;
  qty: number;
}

export interface OrderWristbandDto {
  id: string;
  name: string;
  color: WristbandColor;
  qty: number;
}

export interface OrderLineDto {
  ticketId: string;
  label: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  /** One value-set per ticket unit — index i holds the answers for the i-th ticket of that type. */
  customFieldValues: Record<string, string>[];
}

export type OrderStatus =
  | 'pending_payment'
  | 'awaiting_verification'
  | 'paid'
  | 'expired'
  | 'cancelled'
  | 'rejected';

export interface OrderDto {
  id: string;
  code: string;
  status: OrderStatus;
  lines: OrderLineDto[];
  subtotal: number;
  couponCode: string | null;
  couponDiscount: number;
  total: number;
  qty: number;
  entries: number;
  secondsLeft: number;
  expiresAt: string | null;
  checkedInAt: string | null;
  /**
   * TICK-062 — 1 = legacy (the ticket goes to the contacts checkout collected), 2 = verified
   * delivery. Stamped on the order at creation and immutable, so it is authoritative here and does
   * not depend on the separate `delivery/public` request, which can be slow, fail, or 404 when the
   * rollout flag is off. See `deliveryContract` in `OrderStatus`.
   */
  deliveryContractVersion: number;
  qrPayload: string | null;
  /**
   * TICK-085 — the event this order belongs to is still in UAT, so every page showing it carries
   * the preview strip. Read off the order rather than off a held pass: a tester who opened the
   * link from their SMS has no pass, and an unmarked ticket for an event that is not on sale is
   * exactly the screenshot TICK-081's strip exists to prevent.
   */
  preview: boolean;
  promptpayPayload: string | null;
  paymentAccount: OrderPaymentAccountDto | null;
  /**
   * TICK-060 — returned exactly once, by order creation. Every mutation the buyer can make after
   * checkout authenticates with this rather than with the order code, so it must be kept out of
   * the URL and out of anything that gets shared: see `rememberManagementToken`.
   */
  managementToken?: string;
}

/* ---------------------------------------------------------------- */
/* TICK-060 — post-checkout delivery                                  */
/* ---------------------------------------------------------------- */

export type DeliveryChannel = 'line' | 'email' | 'sms';
export type DeliveryChannelStatus = 'pending' | 'challenge_sent' | 'verified' | 'failed' | 'revoked';
export type LineFriendshipStatus = 'unknown' | 'friend' | 'not_friend' | 'blocked';

export interface DeliveryChannelOptionDto {
  channel: DeliveryChannel;
  available: boolean;
  recipientMasked: string | null;
  unavailableReason: 'NO_RECIPIENT' | 'VERIFICATION_UNAVAILABLE' | 'CHANNEL_DISABLED' | null;
}

export interface DeliveryStateDto {
  orderCode: string;
  payment: OrderStatus;
  /** Paid AND the primary channel verified — the only condition under which a real QR exists. */
  ticketReady: boolean;
  /** TICK-090 — no channel to choose: the ticket is on the page. The panel renders nothing. */
  instantTicket: boolean;
  primary: {
    channel: DeliveryChannel;
    status: DeliveryChannelStatus;
    recipientMasked: string;
    verifiedAt: string | null;
  } | null;
  options: DeliveryChannelOptionDto[];
  line: { bound: boolean; friendship: LineFriendshipStatus; addFriendUrl: string | null } | null;
}

/** The unauthenticated projection: channel state only, deliberately no recipient of any kind. */
export interface PublicDeliveryStateDto {
  orderCode: string;
  eventSlug: string;
  payment: OrderStatus;
  ticketReady: boolean;
  /**
   * 1 = sold under the legacy contract (ticket goes to the contacts checkout collected);
   * 2 = sold under verified delivery. The panel only applies to 2 — see `DeliveryChannelPanel`.
   */
  deliveryContractVersion: number;
  primary: { channel: DeliveryChannel; status: DeliveryChannelStatus } | null;
  line: { bound: boolean; friendship: LineFriendshipStatus } | null;
  /** TICK-090 — this event shows the ticket on the order page instead of sending it to a channel. */
  instantTicket: boolean;
}

export interface DeliveryChallengeDto {
  /** Safe to hold: the OTP form has to name the challenge it is answering. The secret is not here. */
  challengeId: string;
  kind: 'email_magic_link' | 'sms_otp';
  expiresAt: string;
  nextResendAt: string | null;
  /**
   * TICK-063 — true only when the provider accepted the message. False means the code is not coming,
   * so the UI must not tell the customer to go and look for it.
   */
  delivered: boolean;
  deliveryBlockedReason: 'provider_disabled' | 'provider_rejected' | 'no_recipient' | null;
  state: DeliveryStateDto;
}

/**
 * sessionStorage, not localStorage: the capability should not outlive the browsing session that
 * created it, and it is never put in the URL — a link the customer shares would otherwise carry the
 * right to change where their ticket is delivered.
 */
const managementTokenKey = (orderCode: string) => `jamtick.mgmt.${orderCode}`;

export function rememberManagementToken(orderCode: string, token: string | undefined): void {
  if (!token) return;
  try {
    sessionStorage.setItem(managementTokenKey(orderCode), token);
  } catch {
    // Private-mode Safari throws on write. Losing the token degrades to the recovery path rather
    // than breaking checkout, so this is deliberately silent.
  }
}

export function readManagementToken(orderCode: string): string | null {
  try {
    return sessionStorage.getItem(managementTokenKey(orderCode));
  } catch {
    return null;
  }
}

export interface AdminOrderDto extends OrderDto {
  name: string;
  phone: string;
  email: string;
  hasSlip: boolean;
  submittedAt: string | null;
  verifiedAt: string | null;
  alreadyCheckedIn?: boolean;
  /** True when a check-in attempt was blocked because the slip hasn't been admin-verified yet. */
  unverified?: boolean;
  consentAt: string | null;
  consentVersion: string | null;
  /** Wristbands (with quantity) the holder should receive at check-in, aggregated across all ticket lines. */
  wristbands: OrderWristbandDto[];
  /** Staff label that checked this order in (null if not checked in, or checked in before this field existed). */
  checkedInBy: string | null;
  /** Most recent slip-verification attempt, if any — lets the queue show *why* an order is pending. */
  slipVerification: {
    vendor: string;
    status: string;
    createdAt: string;
    /**
     * TICK-137 — only sent for `status: 'duplicate'`: where the verdict came from.
     *
     * - `cross_order` — the reference is on another order. Real reuse; approve stays blocked.
     * - `self_inflicted` — we had already called the vendor about this order (a timeout is the
     *   classic shape), so its "I have seen this slip" is our own traffic.
     * - `unattributable` — the vendor said duplicate on our *first* call, so it was not us, and the
     *   other sighting is not in our records either. Held rather than rejected, but staff are
     *   warned: this is the one that might really be a spent slip.
     */
    duplicateProvenance?: 'cross_order' | 'self_inflicted' | 'unattributable';
  } | null;
}

export interface CouponResultDto {
  code: string;
  pct: number | null;
  flat: number | null;
  subtotal?: number;
  eligibleSubtotal?: number;
  discount: number;
  total?: number;
  message: string;
}

export interface AdminTicketDto {
  id: string;
  nameEn: string;
  nameTh: string;
  priceRegular: number;
  priceEarly: number;
  capacity: number;
  sold: number;
  remaining: number;
  seats: number;
  sort: number;
  closed: boolean;
  visible: boolean;
  promotion: boolean;
  /** TICK-077 — sold as a printed card over a counter; hidden from the public storefront. */
  offline?: boolean;
  /** TICK-078 — the 4-character head of every serial this type's runs print. Null until set. */
  serialPrefix?: string | null;
  soldOut: boolean;
  customFields: TicketCustomFieldDto[];
}

export interface TicketInput {
  nameEn: string;
  nameTh: string;
  priceRegular: number;
  priceEarly: number;
  capacity: number;
  seats: number;
  sort: number;
  visible: boolean;
  promotion: boolean;
  /** TICK-077 — sold as a printed card over a counter; hidden from the public storefront. */
  offline?: boolean;
  /** TICK-078 — 4 chars, A-Z/0-9, unique per event. Required before an offline type can be printed. */
  serialPrefix?: string | null;
  customFields: TicketCustomFieldDto[];
}

export interface CheckinTokenDto {
  id: string;
  role: 'checkin' | 'sales' | 'pos';
  /** Decrypted raw token, owner-only. Null only for legacy rows issued before reveal support existed. */
  token: string | null;
  tokenPreview: string;
  holderLabel: string;
  kind: 'reusable' | 'single';
  expiresAt: string | null;
  revoked: boolean;
  expired: boolean;
  usedUp: boolean;
  active: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface CheckinLogEntryDto {
  id: string;
  actor: string;
  action: string;
  detail: string;
  createdAt: string;
}
export interface CheckinLogDto {
  entries: CheckinLogEntryDto[];
  byStaff: { actor: string; count: number; lastCheckedInAt: string }[];
}

export interface SlipVerificationStatsDto {
  byVendor: {
    easyslip: number;
    thunder: number;
    manual: number;
    mock: number;
  };
  totalVerified: number;
  totalFailed: number;
}

export interface SlipVerificationAuditDto {
  id: string;
  eventId: string;
  eventName: string;
  eventSlug: string;
  orderId: string;
  orderCode: string;
  vendor: string;
  status: string;
  transactionRefMasked: string | null;
  receiverAccountMasked: string | null;
  amount: number | null;
  createdAt: string;
  expiresAt: string;
}

/** TICK-048 — one captured vendor response. The body is fetched separately, via an audited reveal. */
export interface VendorExchangeDto {
  id: string;
  vendor: string;
  operation: string;
  eventId: string | null;
  orderId: string | null;
  orderCode: string | null;
  requestPath: string;
  httpStatus: number | null;
  durationMs: number;
  outcome: string;
  captureMode: 'redacted' | 'full';
  bodyTruncated: boolean;
  hasBody: boolean;
  createdAt: string;
  expiresAt: string;
}

export interface VendorCaptureSettingsDto {
  mode: 'off' | 'redacted' | 'full';
  fullModeUntil: string | null;
}

/** TICK-051 — masked previews only; the plaintext key is never in this shape. */
export interface EventVendorKeysDto {
  thunderPreview: string | null;
  easySlipPreview: string | null;
  primary: 'easyslip' | 'thunder';
}

export interface EventVendorKeysPatchDto {
  thunderApiKey?: string | null;
  easySlipApiKey?: string | null;
  primary?: 'easyslip' | 'thunder';
}

export interface NotificationDeliveryDto {
  id: string;
  eventId: string;
  eventName?: string;
  eventSlug?: string;
  orderId: string;
  orderCode: string;
  channel: DeliveryChannel;
  provider: string;
  purpose: string;
  recipientMasked: string;
  status: string;
  providerMessageId: string | null;
  lastProviderTime: string | null;
  lastReceivedAt: string | null;
  lastErrorCode: string | null;
  createdAt: string;
  /** SuperAdmin-only field; Event Admin responses omit it. Never a raw token or URL. */
  ticketLinkId?: string | null;
}

export interface SuperadminOperationDto {
  orderId: string;
  orderCode: string;
  orderStatus: OrderStatus;
  eventId: string;
  eventName: string;
  eventSlug: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  total: number;
  createdAt: string;
  latestActivityAt: string;
  slip: (SlipVerificationAuditDto & { attemptCount: number }) | null;
  deliveries: Array<Omit<NotificationDeliveryDto, 'providerMessageId' | 'ticketLinkId'>>;
  /** SuperAdmin-only opaque id. The actual capability is returned only by audited reveal. */
  ticketLinkId: string | null;
  /** Safe preview generated server-side; never sufficient to use the ticket capability. */
  ticketTokenMasked: string | null;
  ticketLinkExpiresAt: string | null;
  ticketLinkFirstOpenedAt: string | null;
}

export interface SuperadminOperationsPageDto {
  items: SuperadminOperationDto[];
  nextOffset: number | null;
}

export interface AdminStatsDto {
  revenuePaid: number;
  ordersByStatus: Record<OrderStatus, number>;
  /** rejected orders + awaiting_verification orders whose latest slip check came back a definitive negative — see the "problem" bucket filter. */
  problemCount: number;
  zones: {
    id: string; nameEn: string; nameTh: string;
    capacity: number; sold: number; remaining: number; revenue: number;
  }[];
  checkin: { checkedIn: number; totalPaid: number };
  coupons: AdminCouponDto[];
  today: { revenue: number; ordersPaid: number };
  slipVerificationStats?: SlipVerificationStatsDto;
  generatedAt: string;
}

export type RevenueSeriesRange = '7d' | '14d' | '30d' | 'all';

export interface AdminRevenueSeriesPointDto {
  date: string;
  revenue: number;
  orders: number;
}

export type ResellerCommissionType = 'flat_per_ticket' | 'pct_discounted_price';

export interface ResellerPartnerDto {
  partnerName: string;
  contactInfo: string;
  commissionType: ResellerCommissionType;
  commissionFlat: number | null;
  commissionPct: number | null;
}

export interface ResellerPartnerInput {
  partnerName: string;
  contactInfo?: string | null;
  commissionType: ResellerCommissionType;
  commissionFlat?: number | null;
  commissionPct?: number | null;
}

export interface ResellerReportRowDto {
  code: string;
  partnerName: string;
  contactInfo: string;
  commissionType: ResellerCommissionType;
  commissionFlat: number | null;
  commissionPct: number | null;
  ticketsSold: number;
  ordersPaid: number;
  grossRevenue: number;
  commissionOwed: number;
}

export interface AdminCouponDto {
  code: string;
  pct: number | null;
  flat: number | null;
  usesLeft: number | null;
  active: boolean;
  minSubtotal?: number;
  eligibilityMode?: 'all' | 'selected';
  eligibleTicketIds?: string[] | null;
  ruleVersion?: number;
  uses?: number;
  paidUses?: number;
  totalDiscount?: number;
  isReseller?: boolean;
  resellerPartnerName?: string | null;
  reseller?: ResellerPartnerDto | null;
}

export interface CouponInput {
  code: string;
  pct?: number | null;
  flat?: number | null;
  usesLeft?: number | null;
  minSubtotal?: number | null;
  eligibleTicketIds?: string[] | null;
  reseller?: ResellerPartnerInput | null;
}

export interface UpdateCouponRulesInput {
  minSubtotal?: number | null;
  eligibleTicketIds?: string[] | null;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, withPreviewPass(path, init));
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const issue = Array.isArray(body?.details) ? body.details[0] : undefined;
    const detail = issue ? ` (${[...issue.path].join('.')}: ${issue.message})` : '';
    throw new ApiError(res.status, (body?.error ?? `Request failed (${res.status})`) + detail);
  }
  return body as T;
}

function sendJson<T>(
  method: string,
  path: string,
  data?: unknown,
  headers?: Record<string, string>,
): Promise<T> {
  return request<T>(path, {
    method,
    headers: { 'Content-Type': 'application/json', ...headers },
    body: data === undefined ? undefined : JSON.stringify(data),
  });
}

const postJson = <T>(path: string, data: unknown, headers?: Record<string, string>) =>
  sendJson<T>('POST', path, data, headers);

const DEFAULT_EVENT_SLUG = 'moonfield-fest';

/**
 * `/api/e/<slug>` for all events (defaulting to `moonfield-fest`).
 */
const base = (eventSlug?: string) => `/api/e/${encodeURIComponent(eventSlug || DEFAULT_EVENT_SLUG)}`;

/*
 * TICK-081 — the preview pass, and why it is attached down here rather than at each call site.
 *
 * `eventSalesGate` reads `Authorization`, and until now no customer-facing page had anything to put
 * there — which is why a UAT event could be checked with curl and not in a browser. The pass is a
 * short-lived credential minted from an authenticated admin session; while one is held for an
 * event, every public call scoped to that event carries it, and the gate sees a staff actor.
 *
 * `sessionStorage`, not `localStorage`: a pass is for one testing session in one tab, and should
 * not outlive the window it was opened in. Never attached over a caller's own `Authorization`, so
 * an admin request keeps using the admin token.
 */
const PREVIEW_PASS_PREFIX = 'jamtick.previewPass:';

const previewPassKey = (eventSlug?: string) => `${PREVIEW_PASS_PREFIX}${eventSlug || DEFAULT_EVENT_SLUG}`;

export function getPreviewPass(eventSlug?: string): string | null {
  try {
    return sessionStorage.getItem(previewPassKey(eventSlug));
  } catch {
    return null;
  }
}

export function clearPreviewPass(eventSlug?: string): void {
  try {
    sessionStorage.removeItem(previewPassKey(eventSlug));
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

/**
 * Moves a `?preview=` pass out of the URL and into session storage, and reports whether it did.
 *
 * Out of the URL because a bare token in a location bar is copied into chat messages, bookmarked
 * and sent as a referrer — the caller strips it from history right after this returns.
 */
export function capturePreviewPass(search: string, eventSlug?: string): boolean {
  const token = new URLSearchParams(search).get('preview');
  if (!token || !token.trim()) return false;
  try {
    sessionStorage.setItem(previewPassKey(eventSlug), token.trim());
    return true;
  } catch {
    return false;
  }
}

/** Which event a public API path is scoped to — `/api/e/<slug>/…`, or the default event for `/api/…`. */
function eventSlugForPath(path: string): string {
  const match = /^\/api\/e\/([^/?#]+)/.exec(path);
  return match ? decodeURIComponent(match[1]) : DEFAULT_EVENT_SLUG;
}

function withPreviewPass(path: string, init?: RequestInit): RequestInit | undefined {
  if (!path.startsWith('/api/')) return init;
  const headers = new Headers(init?.headers);
  if (headers.has('Authorization')) return init;
  const pass = getPreviewPass(eventSlugForPath(path));
  if (!pass) return init;
  headers.set('Authorization', `Bearer ${pass}`);
  return { ...init, headers };
}

/*
 * TICK-075 — carrying the sales channel from the landing page to the order.
 *
 * The `?ref=` lands on the event page and the order is created some minutes later, so it has to
 * survive in between. `sessionStorage` rather than a cookie or `localStorage`: this is one shopping
 * session's provenance, not a profile, and it should die with the tab.
 *
 * **Last touch, per the decision record.** Arriving with a `ref` overwrites whatever was stored;
 * arriving without one leaves it alone, so an internal navigation does not erase a real referral.
 */
const SALES_REF_KEY = 'jamtick.salesRef';

/** Set once a click has been reported, so a refresh is not a second visitor. */
const SALES_VISIT_KEY = 'jamtick.salesVisit';

export function captureSalesRef(search: string): void {
  try {
    const ref = new URLSearchParams(search).get('ref');
    if (ref && ref.trim()) sessionStorage.setItem(SALES_REF_KEY, ref.trim().slice(0, 64));
  } catch { /* private browsing, storage disabled — attribution is never worth a thrown error */ }
}

/*
 * TICK-084 — counting the arrival, and the detour through the organizer's LINE OA.
 *
 * Two jobs, one entry point, because they are the same decision seen from either side of a query
 * parameter and splitting them invites a caller to do one without the other.
 *
 * Neither ever throws into the page. A landing page that fails because a counter failed has traded
 * the sale for the statistic, which is the wrong way round for a feature whose entire purpose is to
 * measure sales.
 */
export function reportSalesVisit(eventSlug: string | undefined, search: string): void {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return;
  }
  const ref = (params.get('ref') ?? '').trim().slice(0, 64);
  if (!ref) return;

  /*
   * TICK-095 — this arrival came back from an internal LINE hop, which counted it server-side.
   *
   * Checked before anything else and before the dedupe, because the `ref` on such an arrival has
   * usually been *rewritten* to the link that introduced this visitor. Counting it here would add a
   * click to a Facebook link nobody touched, once per Rich Menu tap. `captureSalesRef` above has
   * already stored the rewritten code, which is the whole point of the hop.
   */
  if (params.get('hop') === 'line') return;

  // The detour leaves this page entirely, so the server counts that arrival on the redirect itself.
  // Counting here as well would report one visitor twice.
  if (params.get('redirect_to_line') === 'true') {
    window.location.replace(`${base(eventSlug)}/line-route?ref=${encodeURIComponent(ref)}`);
    return;
  }

  // TICK-095 — the same shape, in the other direction: a link that lives inside the organizer's OA
  // asks the server who this visitor is before the page decides which code to remember.
  if (params.get('from_line') === 'true') {
    window.location.replace(`${base(eventSlug)}/line-source?ref=${encodeURIComponent(ref)}`);
    return;
  }

  try {
    // Deduped per session and per code: a refresh, or a hop back to the landing page mid-checkout,
    // is the same arrival. A *different* code in the same session is a real second referral and is
    // counted, which keeps this consistent with last-touch.
    if (sessionStorage.getItem(SALES_VISIT_KEY) === ref) return;
    sessionStorage.setItem(SALES_VISIT_KEY, ref);
  } catch { /* storage disabled — count it rather than lose it; a small overcount beats a blind spot */ }

  void postJson(`${base(eventSlug)}/channel-visit`, { ref }).catch(() => { /* never the page's problem */ });
}

/** Read once, at the moment an order is created. Never blocks or fails the order. */
function takeSalesRef(): string | undefined {
  try {
    return sessionStorage.getItem(SALES_REF_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * TICK-074 — an event that is still in UAT, announced but not reachable.
 *
 * Deliberately not a `Partial<PublicEventDto>`: there is no slug and there never will be, because
 * the event answers 404 on every public route while it is `uat`. A separate type is what stops a
 * card from being handed to code that expects something buyable.
 */
export interface OfflineBatchDto {
  id: string;
  ticket_id: string;
  label: string;
  serial_prefix: string;
  quantity: number;
  /** TICK-078 — "พิมพ์ครั้งที่ N" for this ticket type. Assigned once and never reused. */
  run_no: number;
  created_at: string;
  voided_at: string | null;
  unsold: number;
  sold: number;
  voided: number;
}

/** TICK-080 — what an owner sees before deciding to print more, per ticket type. */
export interface OfflineSummaryDto {
  ticketId: string;
  name: string;
  serialPrefix: string | null;
  capacity: number;
  /** Seats `tickets.sold` is holding — what the storefront actually checks. */
  reserved: number;
  remaining: number;
  printed: number;
  sold: number;
  unsold: number;
  voided: number;
  /** Normally 0. Cards that exist holding no seat — selling them admits people nobody planned for. */
  unreserved: number;
}

export type OfflineCardSkipDto = {
  serial: string;
  reason: 'VOID' | 'ALREADY_SOLD' | 'NOT_SOLD' | 'CHECKED_IN' | 'RACED';
};

export interface OfflineCardDto {
  id: string;
  serial: string;
  status: 'unsold' | 'sold' | 'void';
  activatedAt?: string | null;
  activatedBy?: string | null;
  orderId?: string | null;
  payload: string;
}

/** Where the link was posted. How it travels is `SalesChannelLineRoute`, which is a separate fact. */
export type SalesChannelPlatform = 'facebook' | 'instagram' | 'tiktok' | 'line' | 'other';

/**
 * TICK-095 — how a link travels through the organizer's LINE OA, as one value of three.
 *
 * `into_oa` sends the visitor to the add-friend page and remembers who they are. `inside_oa` is a
 * link that lives in the OA — a Rich Menu button — and asks who introduced them. They are mutually
 * exclusive, which is why this is one value rather than two booleans on the wire.
 */
export type SalesChannelLineRoute = 'none' | 'into_oa' | 'inside_oa';

export function salesChannelLineRouteOf(c: SalesChannelDto): SalesChannelLineRoute {
  if (c.redirect_to_line) return 'into_oa';
  if (c.resolve_from_line) return 'inside_oa';
  return 'none';
}

export interface SalesChannelDto {
  id: string;
  code: string;
  platform: SalesChannelPlatform;
  label: string;
  /** TICK-084 — the link detours through the organizer's LINE OA before the event page. */
  redirect_to_line?: number;
  /** TICK-095 — the link lives inside the OA, and asks LINE which link introduced this visitor. */
  resolve_from_line?: number;
  archivedAt?: string | null;
  archived_at?: string | null;
}

export interface SalesChannelReportDto {
  channelId: string | null;
  code: string | null;
  platform: string | null;
  label: string;
  /**
   * TICK-084. Null on the unresolved and direct rows, which have no channel and therefore no link
   * to have been clicked. Rendered as an em dash — a zero would claim we measured and found none.
   */
  redirectToLine: boolean | null;
  clicks: number | null;
  lineRedirects: number | null;
  orders: number;
  paidOrders: number;
  revenue: number;
}

/** TICK-084 — one channel on one day, for the history chart. */
export interface SalesChannelDailyDto {
  channelId: string;
  day: string;
  clicks: number;
  lineRedirects: number;
  orders: number;
  paidOrders: number;
  revenue: number;
}

export interface PortalTeaserDto {
  id: string;
  name: string;
  dateLabel: string;
  tagline: string;
  heroImageUrl: string | null;
  /** Optional for the same reason it is on `PublicEventDto`: absent means "shown", never "hidden". */
  carouselVisible?: boolean;
}

export interface PublicEventDto {
  id: string;
  slug: string;
  name: string;
  dateLabel: string;
  status: 'active' | 'paused' | 'archived';
  tagline: string;
  heroImageUrl: string | null;
  minPrice: number;
  phase: 'early-bird' | 'regular';
  phaseLabel: string;
  tickets: TicketDto[];
  lineup: { name: string; time: string; photoUrl: string | null }[];
  portalVisible?: boolean;
  carouselVisible?: boolean;
  sortOrder?: number;
  endsAt?: string | null;
  timezone?: string | null;
  transactionReferencePrefix?: string;
}

/**
 * TICK-042 — `public` sells normally, `uat` 404s every public route unless the caller holds a
 * staff credential for the event, `closed` refuses writes with the existing SALES_PAUSED error.
 */
export type EventSalesMode = 'public' | 'uat' | 'closed';

export interface OrganizerLeadDto {
  id: string;
  name: string;
  phone: string;
  createdAt: string;
}

export interface ConcertCalendarDto {
  id: string;
  dateLabel: string;
  eventName: string;
  artist: string;
  venue: string;
  province: string;
  monthKey: string;
  sortDate: string;
  createdAt: string;
}

export interface ConcertImportDraftDto {
  eventName: string;
  artist: string;
  venue: string;
  dateLabel: string;
  sourceUrl: string;
}

export interface ConcertCalendarImportRowDto {
  row: number;
  status: 'imported' | 'duplicate' | 'missing_date' | 'invalid';
  eventName: string;
  reason?: string;
}

export interface ConcertCalendarImportResultDto {
  ok: boolean;
  summary: { total: number; imported: number; duplicate: number; missingDate: number; invalid: number };
  rows: ConcertCalendarImportRowDto[];
}

export interface PortalSettingsDto {
  organizerSectionEnabled: boolean;
  organizerSectionTitle: string;
  organizerSectionSubtitle: string;
  organizerCards: Array<{ emoji: string; title: string; desc: string }>;
}

export const api = {
  getPublicEvents: () => request<PublicEventDto[]>('/api/events'),
  getPortalTeasers: () => request<PortalTeaserDto[]>('/api/events/upcoming'),
  getPortalSettings: () => request<PortalSettingsDto>('/api/portal-settings'),
  submitLead: (name: string, phone: string) => postJson<{ ok: boolean }>('/api/leads', { name, phone }),
  getEvent: (eventSlug?: string) => request<EventDto>(`${base(eventSlug)}/event`),
  /** TICK-081 — served from ahead of the sales gate, so it answers while `/event` still 404s. */
  getEventTeaser: (eventSlug?: string) => request<EventTeaserDto>(`${base(eventSlug)}/teaser`),
  validateCoupon: (code: string, items: Record<string, number>, eventSlug?: string) =>
    postJson<CouponResultDto>(`${base(eventSlug)}/coupons/validate`, { code, items }),
  createOrder: (items: Record<string, number>, coupon?: string, eventSlug?: string) =>
    postJson<OrderDto>(`${base(eventSlug)}/orders`, { items, coupon: coupon || undefined, ref: takeSalesRef() }),
  getOrder: (key: string, eventSlug?: string) => request<OrderDto>(`${base(eventSlug)}/orders/${encodeURIComponent(key)}`),
  cancelOrder: (key: string, eventSlug?: string) =>
    request<OrderDto>(`${base(eventSlug)}/orders/${encodeURIComponent(key)}/cancel`, { method: 'POST' }),
  submitOrder: (key: string, form: FormData, eventSlug?: string) =>
    request<OrderDto>(`${base(eventSlug)}/orders/${encodeURIComponent(key)}/submit`, { method: 'POST', body: form }),
};

export const ticketLinkApi = {
  getOrder: (token: string) => request<{ eventSlug: string; order: OrderDto }>(`/api/ticket-links/${encodeURIComponent(token)}/order`),
};

/**
 * TICK-060 — every call carries the buyer's management capability in the Authorization header.
 * A 404 from any of these means the rollout flag is off for this deploy, which callers render as
 * "this section does not exist" rather than as an error.
 */
export const deliveryApi = {
  /**
   * Order-code addressable and unauthenticated, so the page can tell "this deploy has no delivery
   * selection" (404) from "this device has no capability" (200 but no token). Carries no recipient.
   */
  getPublicState: (orderCode: string, eventSlug?: string) =>
    request<PublicDeliveryStateDto>(`${base(eventSlug)}/orders/${encodeURIComponent(orderCode)}/delivery/public`),
  getState: (orderCode: string, token: string, eventSlug?: string) =>
    request<DeliveryStateDto>(`${base(eventSlug)}/orders/${encodeURIComponent(orderCode)}/delivery`, { headers: auth(token) }),
  /**
   * TICK-090 — the ticket itself, for the events that show it on this page.
   *
   * Only ever called when `instantTicket` says the server will answer; it refuses with 409 under
   * the verified-channel journey, where the ticket reaches the buyer as a link instead.
   */
  getInstantTicket: (orderCode: string, token: string, eventSlug?: string) =>
    request<{ order: OrderDto }>(`${base(eventSlug)}/orders/${encodeURIComponent(orderCode)}/delivery/ticket`, { headers: auth(token) }),
  setPrimary: (orderCode: string, channel: DeliveryChannel, token: string, eventSlug?: string) =>
    sendJson<DeliveryStateDto>('PUT', `${base(eventSlug)}/orders/${encodeURIComponent(orderCode)}/delivery/primary`, { channel }, auth(token)),
  /** Undo an unverified selection — the way out for a customer who cannot finish the LINE flow. */
  clearPrimary: (orderCode: string, token: string, eventSlug?: string) =>
    sendJson<DeliveryStateDto>('DELETE', `${base(eventSlug)}/orders/${encodeURIComponent(orderCode)}/delivery/primary`, undefined, auth(token)),
  /** Issues or resends the Magic Link / OTP for the order's chosen email or SMS channel. */
  requestChallenge: (orderCode: string, token: string, eventSlug?: string) =>
    postJson<DeliveryChallengeDto>(`${base(eventSlug)}/orders/${encodeURIComponent(orderCode)}/delivery/challenges`, {}, auth(token)),
  verifyOtp: (orderCode: string, challengeId: string, otp: string, token: string, eventSlug?: string) =>
    postJson<DeliveryStateDto>(`${base(eventSlug)}/orders/${encodeURIComponent(orderCode)}/delivery/sms/verify`, { challengeId, otp }, auth(token)),
  /** Unauthenticated — the token in the Magic Link is itself the credential. */
  verifyEmail: (token: string) =>
    postJson<{
      verification: 'verified' | 'superseded';
      alreadyVerified?: boolean;
      channel?: DeliveryChannel | null;
      managementToken?: string;
      state?: PublicDeliveryStateDto;
    }>('/api/delivery/email/verify', { token }),
  /**
   * R4 — asks for a fresh capability to be sent to the contact already verified on the order. The
   * response is deliberately the same whatever the outcome, so it cannot confirm an order exists.
   */
  requestRecovery: (orderCode: string, channel: 'email' | 'sms', eventSlug?: string) =>
    postJson<{ ok: boolean; message: string }>(`${base(eventSlug)}/orders/${encodeURIComponent(orderCode)}/recovery/challenges`, { channel }),
  startLineBinding: (orderCode: string, token: string, eventSlug?: string) =>
    postJson<{ authorizationUrl: string }>(`${base(eventSlug)}/orders/${encodeURIComponent(orderCode)}/delivery/line/authorize`, {}, auth(token)),
  refreshLineFriendship: (orderCode: string, token: string, eventSlug?: string) =>
    postJson<{ authorizationUrl: string }>(`${base(eventSlug)}/orders/${encodeURIComponent(orderCode)}/delivery/line/friendship/refresh`, {}, auth(token)),
};

const auth = (token: string) => ({ Authorization: `Bearer ${token}` });

export const adminApi = {
  listOrders: (token: string, status?: string, q?: string, eventSlug?: string, checkedIn?: boolean, bucket?: 'problem') => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (checkedIn) params.set('checkedIn', 'true');
    if (bucket) params.set('bucket', bucket);
    if (q) params.set('q', q);
    const qs = params.toString();
    return request<AdminOrderDto[]>(`${base(eventSlug)}/admin/orders${qs ? `?${qs}` : ''}`, { headers: auth(token) });
  },
  downloadOrdersCsv: async (
    token: string,
    eventSlug?: string,
    status?: string,
    checkedIn?: boolean,
    q?: string,
    bucket?: 'problem',
  ): Promise<void> => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (checkedIn) params.set('checkedIn', 'true');
    if (bucket) params.set('bucket', bucket);
    if (q) params.set('q', q);
    const qs = params.toString();
    const res = await fetch(`${base(eventSlug)}/admin/orders.csv${qs ? `?${qs}` : ''}`, { headers: auth(token) });
    if (!res.ok) throw new ApiError(res.status, 'Failed to download CSV');
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    const filename = match?.[1] ?? `jamtick-attendees-${new Date().toISOString().slice(0, 10)}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  verify: (token: string, key: string, eventSlug?: string) =>
    postJson<AdminOrderDto>(`${base(eventSlug)}/admin/orders/${encodeURIComponent(key)}/verify`, {}, auth(token)),
  reject: (token: string, key: string, eventSlug?: string) =>
    postJson<AdminOrderDto>(`${base(eventSlug)}/admin/orders/${encodeURIComponent(key)}/reject`, {}, auth(token)),
  reverify: (token: string, key: string, eventSlug?: string) =>
    postJson<{ order: AdminOrderDto; vendor: string; status: string }>(
      `${base(eventSlug)}/admin/orders/${encodeURIComponent(key)}/reverify`, {}, auth(token),
    ),
  /** TICK-045 — re-arms the existing outbox row, so pressing twice cannot send two tickets. */
  resendTicket: (token: string, key: string, channel: DeliveryChannel | 'all', eventSlug?: string) =>
    postJson<{ ok: boolean; requeued: DeliveryChannel[]; skipped: { channel: string; reason: string }[]; order: AdminOrderDto }>(
      `${base(eventSlug)}/admin/orders/${encodeURIComponent(key)}/resend`, { channel }, auth(token),
    ),
  checkinLookup: (token: string, payload: string, eventSlug?: string) =>
    postJson<AdminOrderDto>(`${base(eventSlug)}/admin/checkin/lookup`, { payload }, auth(token)),
  checkinConfirm: (token: string, payload: string, expectedEntries: number, eventSlug?: string) =>
    postJson<AdminOrderDto>(`${base(eventSlug)}/admin/checkin/confirm`, { payload, confirmWholeOrder: true, expectedEntries }, auth(token)),
  /** R7 — revokes every delivered QR/link for this order and issues a replacement. Owner only. */
  reissueCredential: (token: string, key: string, reason: string, idempotencyKey: string, eventSlug?: string) =>
    postJson<{ ok: boolean; idempotent?: boolean; credentialVersion?: number; revokedTicketLinks?: number; order: AdminOrderDto }>(
      `${base(eventSlug)}/admin/orders/${encodeURIComponent(key)}/credential/reissue`,
      { reason, idempotencyKey }, auth(token),
    ),
  /** TICK-075 — the registered links an organizer hands to content creators. Owner-only. */
  listSalesChannels: (token: string, eventSlug?: string) =>
    request<SalesChannelDto[]>(`${base(eventSlug)}/admin/sales-channels`, { headers: auth(token) }),
  createSalesChannel: (
    token: string,
    input: {
      code: string; platform: SalesChannelPlatform; label: string;
      redirectToLine?: boolean; resolveFromLine?: boolean;
    },
    eventSlug?: string,
  ) => postJson<SalesChannelDto>(`${base(eventSlug)}/admin/sales-channels`, input, auth(token)),
  /**
   * TICK-095 — correct a link's LINE route without retiring it.
   *
   * One value, not two flags: the pair "into the OA" and "inside the OA" is a loop, and a shape
   * that cannot express it beats an error that reports it.
   */
  setSalesChannelLineRoute: (
    token: string, id: string, route: SalesChannelLineRoute, eventSlug?: string,
  ) => sendJson<SalesChannelDto>(
    'PATCH', `${base(eventSlug)}/admin/sales-channels/${encodeURIComponent(id)}/line-route`,
    { route }, auth(token),
  ),
  archiveSalesChannel: (token: string, id: string, eventSlug?: string) =>
    postJson<{ ok: boolean }>(`${base(eventSlug)}/admin/sales-channels/${encodeURIComponent(id)}/archive`, {}, auth(token)),
  /** Orders per channel — not visits, and therefore not a conversion rate. See TICK-075. */
  getSalesChannelReport: (token: string, eventSlug?: string) =>
    request<SalesChannelReportDto[]>(`${base(eventSlug)}/admin/sales-channels/report`, { headers: auth(token) }),
  /** TICK-084 — the same numbers by day, for the history chart. */
  getSalesChannelDaily: (token: string, days: number, eventSlug?: string) =>
    request<SalesChannelDailyDto[]>(`${base(eventSlug)}/admin/sales-channels/daily?days=${days}`, { headers: auth(token) }),
  /* TICK-077 — physical cards, printed before anyone buys them. */
  listOfflineBatches: (token: string, eventSlug?: string) =>
    request<OfflineBatchDto[]>(`${base(eventSlug)}/admin/offline-batches`, { headers: auth(token) }),
  createOfflineBatch: (
    token: string,
    input: { ticketId: string; label: string; quantity: number },
    eventSlug?: string,
  ) => postJson<OfflineBatchDto>(`${base(eventSlug)}/admin/offline-batches`, input, auth(token)),
  getOfflineSummary: (token: string, eventSlug?: string) =>
    request<OfflineSummaryDto[]>(`${base(eventSlug)}/admin/offline-summary`, { headers: auth(token) }),
  /** TICK-080 — sell just the cards the owner ticked. */
  activateOfflineSelected: (token: string, cardIds: string[], eventSlug?: string) =>
    postJson<{ activated: number; skipped: OfflineCardSkipDto[] }>(
      `${base(eventSlug)}/admin/offline-tickets/activate-selected`, { cardIds }, auth(token)),
  /** Put sold cards back on the shelf. Refused for a card whose order walked through the gate. */
  deactivateOfflineSelected: (token: string, cardIds: string[], eventSlug?: string) =>
    postJson<{ deactivated: number; skipped: OfflineCardSkipDto[] }>(
      `${base(eventSlug)}/admin/offline-tickets/deactivate`, { cardIds }, auth(token)),
  /** The serials and payloads; the console draws and zips them — see TICK-077 on why not the Worker. */
  listOfflineCards: (token: string, batchId: string, eventSlug?: string) =>
    request<OfflineCardDto[]>(`${base(eventSlug)}/admin/offline-batches/${encodeURIComponent(batchId)}/cards`, { headers: auth(token) }),
  /**
   * TICK-078 — sell a whole run, one slice per call.
   *
   * The caller loops until `remaining` is zero. A run holds up to 2000 cards and each is an order;
   * doing that in one request is the shape of work the Worker was measured cancelling at ~2s.
   */
  activateOfflineBatch: (token: string, batchId: string, eventSlug?: string) =>
    postJson<{ activated: number; remaining: number }>(
      `${base(eventSlug)}/admin/offline-batches/${encodeURIComponent(batchId)}/activate`, {}, auth(token)),
  voidOfflineBatch: (token: string, batchId: string, eventSlug?: string) =>
    postJson<{ voided: number }>(`${base(eventSlug)}/admin/offline-batches/${encodeURIComponent(batchId)}/void`, {}, auth(token)),
  /** Sell one card at the counter. Owner or staff — this is the person taking the cash. */
  activateOfflineTicket: (token: string, serial: string, eventSlug?: string) =>
    postJson<{ ok: boolean; alreadySold: boolean; serial: string; order: AdminOrderDto }>(
      `${base(eventSlug)}/admin/offline-tickets/activate`, { serial }, auth(token)),
  getStats: (token: string, eventSlug?: string) =>
    request<AdminStatsDto>(`${base(eventSlug)}/admin/stats`, { headers: auth(token) }),
  getRevenueSeries: (token: string, range: RevenueSeriesRange, eventSlug?: string) =>
    request<AdminRevenueSeriesPointDto[]>(`${base(eventSlug)}/admin/revenue-series?range=${range}`, { headers: auth(token) }),
  listNotificationDeliveries: (token: string, eventSlug?: string) =>
    request<NotificationDeliveryDto[]>(`${base(eventSlug)}/admin/notification-deliveries`, { headers: auth(token) }),
  getConcertCalendar: (token: string, eventSlug?: string) =>
    request<ConcertCalendarDto[]>(`${base(eventSlug)}/admin/concert-calendar`, { headers: auth(token) }),
  listTickets: (token: string, eventSlug?: string) =>
    request<AdminTicketDto[]>(`${base(eventSlug)}/admin/tickets`, { headers: auth(token) }),
  createTicket: (token: string, input: TicketInput, eventSlug?: string) =>
    sendJson<AdminTicketDto>('POST', `${base(eventSlug)}/admin/tickets`, input, auth(token)),
  updateTicket: (token: string, id: string, patch: Partial<TicketInput> & { closed?: boolean }, eventSlug?: string) =>
    sendJson<AdminTicketDto>('PATCH', `${base(eventSlug)}/admin/tickets/${encodeURIComponent(id)}`, patch, auth(token)),
  deleteTicket: (token: string, id: string, eventSlug?: string) =>
    sendJson<{ ok: boolean }>('DELETE', `${base(eventSlug)}/admin/tickets/${encodeURIComponent(id)}`, undefined, auth(token)),
  listWristbands: (token: string, eventSlug?: string) =>
    request<WristbandDto[]>(`${base(eventSlug)}/admin/wristbands`, { headers: auth(token) }),
  createWristband: (token: string, name: string, color: WristbandColor, eventSlug?: string) =>
    sendJson<WristbandDto>('POST', `${base(eventSlug)}/admin/wristbands`, { name, color }, auth(token)),
  updateWristband: (token: string, id: string, patch: { name?: string; color?: WristbandColor }, eventSlug?: string) =>
    sendJson<WristbandDto>('PATCH', `${base(eventSlug)}/admin/wristbands/${encodeURIComponent(id)}`, patch, auth(token)),
  deleteWristband: (token: string, id: string, eventSlug?: string) =>
    sendJson<{ ok: boolean }>('DELETE', `${base(eventSlug)}/admin/wristbands/${encodeURIComponent(id)}`, undefined, auth(token)),
  getTicketWristbands: (token: string, eventSlug?: string) =>
    request<Record<string, WristbandBinding[]>>(`${base(eventSlug)}/admin/ticket-wristbands`, { headers: auth(token) }),
  setTicketWristbands: (token: string, ticketId: string, bindings: WristbandBinding[], eventSlug?: string) =>
    sendJson<{ ok: boolean }>('PUT', `${base(eventSlug)}/admin/tickets/${encodeURIComponent(ticketId)}/wristbands`, { bindings }, auth(token)),
  listPaymentAccounts: (token: string, eventSlug?: string) =>
    request<PaymentAccountDto[]>(`${base(eventSlug)}/admin/payment-accounts`, { headers: auth(token) }),
  createPaymentAccount: (token: string, input: PaymentAccountInput, eventSlug?: string) =>
    sendJson<PaymentAccountDto>('POST', `${base(eventSlug)}/admin/payment-accounts`, input, auth(token)),
  updatePaymentAccount: (token: string, id: string, patch: Partial<PaymentAccountInput> & { enabled?: boolean }, eventSlug?: string) =>
    sendJson<PaymentAccountDto>('PATCH', `${base(eventSlug)}/admin/payment-accounts/${encodeURIComponent(id)}`, patch, auth(token)),
  deletePaymentAccount: (token: string, id: string, eventSlug?: string) =>
    sendJson<{ ok: boolean }>('DELETE', `${base(eventSlug)}/admin/payment-accounts/${encodeURIComponent(id)}`, undefined, auth(token)),
  /** Fires a real ฿1 QR from a draft (not yet saved) account so it can be scanned before trusting it. */
  testPaymentAccountQr: (
    token: string,
    input: { provider: PaymentProvider; identifier: string; promptpayType?: 'msisdn' | 'natid' | 'ewallet' | null; merchantName?: string | null },
    eventSlug?: string,
  ) => sendJson<{ payload: string }>('POST', `${base(eventSlug)}/admin/payment-accounts/test-qr`, input, auth(token)),
  getEarlyBird: (token: string, eventSlug?: string) =>
    request<EarlyBirdDto>(`${base(eventSlug)}/admin/early-bird`, { headers: auth(token) }),
  /**
   * The whole Early Bird setting in one call. `patch` is an object rather than a run of optional
   * positionals because the three fields are independent and a caller changing only the last one
   * still has to restate the date — passing them by name is what keeps that honest.
   */
  setEarlyBird: (
    token: string,
    patch: { earlyBirdUntil: string | null; enabled?: boolean; dateVisible?: boolean },
    eventSlug?: string,
  ) => sendJson<EarlyBirdDto>('PUT', `${base(eventSlug)}/admin/early-bird`, patch, auth(token)),
  getCheckoutSettings: (token: string, eventSlug?: string) =>
    request<CheckoutSettingsDto>(`${base(eventSlug)}/admin/checkout-settings`, { headers: auth(token) }),
  setCheckoutSettings: (token: string, reservationMinutes: number | null, eventSlug?: string) =>
    sendJson<CheckoutSettingsDto>('PUT', `${base(eventSlug)}/admin/checkout-settings`, { reservationMinutes }, auth(token)),
  getTicketDelivery: (token: string, eventSlug?: string) =>
    request<TicketDeliveryDto>(`${base(eventSlug)}/admin/ticket-delivery`, { headers: auth(token) }),
  setTicketDelivery: (
    token: string, input: { mode: TicketDeliveryMode; copyChannels: DeliveryChannel[] }, eventSlug?: string,
  ) => sendJson<TicketDeliveryDto>('PUT', `${base(eventSlug)}/admin/ticket-delivery`, input, auth(token)),
  createCoupon: (token: string, input: CouponInput, eventSlug?: string) =>
    sendJson<AdminCouponDto>('POST', `${base(eventSlug)}/admin/coupons`, input, auth(token)),
  updateCouponRules: (token: string, code: string, input: UpdateCouponRulesInput, eventSlug?: string) =>
    sendJson<AdminCouponDto>('PUT', `${base(eventSlug)}/admin/coupons/${encodeURIComponent(code)}/rules`, input, auth(token)),
  setCouponActive: (token: string, code: string, active: boolean, eventSlug?: string) =>
    sendJson<AdminCouponDto>('PATCH', `${base(eventSlug)}/admin/coupons/${encodeURIComponent(code)}`, { active }, auth(token)),
  deleteCoupon: (token: string, code: string, eventSlug?: string) =>
    sendJson<{ ok: boolean }>('DELETE', `${base(eventSlug)}/admin/coupons/${encodeURIComponent(code)}`, undefined, auth(token)),
  setCouponReseller: (token: string, code: string, input: ResellerPartnerInput, eventSlug?: string) =>
    sendJson<AdminCouponDto>('PUT', `${base(eventSlug)}/admin/coupons/${encodeURIComponent(code)}/reseller`, input, auth(token)),
  removeCouponReseller: (token: string, code: string, eventSlug?: string) =>
    sendJson<AdminCouponDto>('DELETE', `${base(eventSlug)}/admin/coupons/${encodeURIComponent(code)}/reseller`, undefined, auth(token)),
  getResellerReport: (token: string, eventSlug?: string) =>
    request<ResellerReportRowDto[]>(`${base(eventSlug)}/admin/coupons/reseller-report`, { headers: auth(token) }),
  slipUrl: (key: string, eventSlug?: string) => `${base(eventSlug)}/admin/orders/${encodeURIComponent(key)}/slip`,
  fetchSlip: async (token: string, key: string, eventSlug?: string): Promise<string> => {
    const res = await fetch(adminApi.slipUrl(key, eventSlug), { headers: auth(token) });
    if (!res.ok) throw new ApiError(res.status, 'Failed to load slip');
    return URL.createObjectURL(await res.blob());
  },
  getContent: (token: string, eventSlug?: string) => request<ContentDto>(`${base(eventSlug)}/admin/content`, { headers: auth(token) }),
  updateContent: (token: string, content: ContentDto, eventSlug?: string) =>
    sendJson<ContentDto>('PUT', `${base(eventSlug)}/admin/content`, content, auth(token)),
  // TICK-081 — the switch lives on the event row, not in the CMS blob, so it saves on its own
  // rather than with the rest of the page: publishing is a different action from writing copy.
  getComingSoon: (token: string, eventSlug?: string) =>
    request<ComingSoonStateDto>(`${base(eventSlug)}/admin/coming-soon`, { headers: auth(token) }),
  setComingSoon: (token: string, enabled: boolean, eventSlug?: string) =>
    sendJson<ComingSoonStateDto>('PUT', `${base(eventSlug)}/admin/coming-soon`, { enabled }, auth(token)),
  createPreviewPass: (token: string, minutes: number, eventSlug?: string) =>
    sendJson<PreviewPassDto>('POST', `${base(eventSlug)}/admin/preview-pass`, { minutes }, auth(token)),
  revokePreviewPass: (token: string, eventSlug?: string) =>
    sendJson<{ ok: boolean }>('DELETE', `${base(eventSlug)}/admin/preview-pass`, undefined, auth(token)),
  uploadContentImage: async (token: string, file: File, eventSlug?: string): Promise<{ key: string }> => {
    const form = new FormData();
    form.append('image', file);
    const res = await fetch(`${base(eventSlug)}/admin/content/images`, { method: 'POST', headers: auth(token), body: form });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw new ApiError(res.status, body?.error ?? 'Failed to upload image');
    return body as { key: string };
  },
  contentImageUrl: (key: string, eventSlug?: string) => `${base(eventSlug)}/content/image/${encodeURIComponent(key)}`,
  listCheckinTokens: (token: string, eventSlug?: string) =>
    request<CheckinTokenDto[]>(`${base(eventSlug)}/admin/tokens`, { headers: auth(token) }),
  createCheckinToken: (token: string, holderLabel: string, expiry: '24h' | '7d' | '30d' | 'never', eventSlug?: string, role: 'checkin' | 'sales' | 'pos' = 'checkin') =>
    sendJson<CheckinTokenDto & { token: string }>('POST', `${base(eventSlug)}/admin/tokens`, { holderLabel, expiry, role }, auth(token)),
  revokeCheckinToken: (token: string, id: string, eventSlug?: string) =>
    sendJson<{ ok: boolean }>('POST', `${base(eventSlug)}/admin/tokens/${encodeURIComponent(id)}/revoke`, undefined, auth(token)),
  getCheckinLog: (token: string, eventSlug?: string) =>
    request<CheckinLogDto>(`${base(eventSlug)}/admin/checkin-log`, { headers: auth(token) }),
  whoami: (token: string, eventSlug?: string) =>
    request<{ role: 'owner' | 'staff' | 'sales'; actorLabel: string | null; pollingEnabled: boolean; pollingIntervalSec: number }>(
      `${base(eventSlug)}/admin/whoami`, { headers: auth(token) },
    ),
};

/** Wristband-desk check-in — accepts CHECKIN_TOKEN or ADMIN_TOKEN (or a DB-issued checkin/owner/staff token), no other admin access. */
export const checkinApi = {
  lookup: (token: string, payload: string, eventSlug?: string) =>
    postJson<AdminOrderDto>(`${base(eventSlug)}/checkin/lookup`, { payload }, auth(token)),
  /**
   * TICK-062: `expectedEntries` is the staff member's statement of how many people they are
   * admitting. The server rejects it if it disagrees with the order, which is what stops a scan
   * taken while half the group was still outside from spending the order's one credential.
   */
  confirm: (token: string, payload: string, expectedEntries: number, eventSlug?: string) =>
    postJson<AdminOrderDto>(`${base(eventSlug)}/checkin/confirm`, { payload, confirmWholeOrder: true, expectedEntries }, auth(token)),
};

/* ==================================================================== */
/* TICK-078 — the counter                                               */
/* ==================================================================== */

export interface PosCardDto {
  serial: string;
  status: 'unsold' | 'sold' | 'void';
  ticketName: string | null;
  price: number | null;
  seats: number;
}

/**
 * Selling stock is not admitting people, so it is its own token role and its own surface.
 *
 * `lookup` is read-only by design: the seller quotes the price, takes the cash, and only then
 * presses the button. Folding the two together would mean a mis-scan sells a card.
 */
export const posApi = {
  lookup: (token: string, code: string, eventSlug?: string) =>
    postJson<PosCardDto>(`${base(eventSlug)}/pos/lookup`, { code }, auth(token)),
  activate: (token: string, code: string, eventSlug?: string) =>
    postJson<{ ok: boolean; alreadySold: boolean; serial: string; order: AdminOrderDto }>(
      `${base(eventSlug)}/pos/activate`, { code }, auth(token)),
};

/* ==================================================================== */
/* Superadmin — platform-level console                                  */
/* ==================================================================== */

export interface PlatformOverviewDto {
  eventCount: number;
  totalRevenue: number;
  totalOrders: number;
  activeTokenCount: number;
  slipVerificationStats?: SlipVerificationStatsDto;
  /** TICK-052: orders whose slip verification exhausted automatic retry and need a manual look. */
  slipVerificationDeadLetterCount?: number;
  coreServices: ServiceStatusDto[];
  providers: ProviderStatusDto[];
  adminPolling: AdminPollingSettingsDto;
}

export interface AdminPollingSettingsDto {
  enabled: boolean;
  intervalSec: number;
}

export interface TenantSummaryDto {
  id: string;
  slug: string;
  name: string;
  status: 'active' | 'paused' | 'archived';
  dateLabel: string;
  plan: 'standard' | 'pro';
  ownerEmail: string;
  isDefault: boolean;
  createdAt: string;
  orders: number;
  revenue: number;
  sold: number;
  capacity: number;
  teamCount: number;
  portalVisible?: boolean;
  /** TICK-074: announce a `uat` event on the portal without making it reachable. */
  portalTeaser?: boolean;
  /** TICK-081 — this event's own URL answers with a coming-soon page. Read-only in Superadmin. */
  teaserPage?: boolean;
  carouselVisible?: boolean;
  /** TICK-042: who may reach this event's public routes. `portalVisible` only hides the listing. */
  salesMode?: EventSalesMode;
  sortOrder?: number;
  heroImageUrl?: string | null;
  endsAt?: string | null;
  timezone?: string | null;
  transactionReferencePrefix?: string;
}

export interface AccessTokenDto {
  id: string;
  /** Full decrypted token — null only for tokens issued before reveal support existed. */
  token: string | null;
  tokenPreview: string;
  role: 'owner' | 'staff' | 'checkin' | 'sales';
  holderLabel: string;
  kind: 'reusable' | 'single';
  eventId: string;
  eventName: string;
  eventSlug: string;
  expiresAt: string | null;
  revoked: boolean;
  expired: boolean;
  usedUp: boolean;
  active: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface AuditRowDto {
  id: string;
  actor: string;
  action: string;
  detail: string;
  eventName: string | null;
  createdAt: string;
}

export interface ServiceStatusDto {
  name: string;
  status: 'operational' | 'degraded' | 'down';
  latencyMs: number | null;
  detail: string;
  checkedAt: string;
}

export type ProviderId = 'easyslip' | 'thunder' | 'resend' | 'thaibulksms';

export interface ProviderStatusDto {
  id: ProviderId;
  name: string;
  purpose: string;
  availability: 'operational' | 'degraded' | 'down' | 'unconfigured' | 'disabled';
  detail: string;
  checkedAt: string;
  capacity: {
    unit: 'verifications' | 'credits';
    remaining: number | null;
    used: number | null;
    limit: number | null;
  } | null;
  monitoringPermission?: 'granted' | 'restricted';
  channelEnabled?: boolean;
}

export interface CreateEventInput {
  name: string;
  slug: string;
  dateLabel: string;
  eventDate?: string;
  endsAt?: string | null;
  timezone?: string | null;
  transactionReferencePrefix?: string;
  plan: 'standard' | 'pro';
  ownerEmail: string;
  template: 'blank' | 'clone' | 'festival' | 'saved';
  templateSourceId?: string;
  salesMode?: EventSalesMode;
}

export interface CreateEventResultDto {
  event: { id: string; slug: string; name: string };
  ownerToken: string;
  adminUrl: string;
}

export interface EventTemplateDto {
  id: string;
  name: string;
  createdAt: string;
}

export interface CreateTokenInput {
  eventId: string;
  role: 'owner' | 'staff' | 'checkin' | 'sales';
  holderLabel: string;
  kind: 'reusable' | 'single';
  expiry: '24h' | '7d' | '30d' | 'event' | 'never';
}

export interface CreateTokenResultDto {
  id: string;
  token: string;
  tokenPreview: string;
}

export const superadminApi = {
  getOverview: (token: string, refresh = false) =>
    request<PlatformOverviewDto>(`/api/superadmin/overview${refresh ? '?refresh=1' : ''}`, { headers: auth(token) }),
  getRevenueSeries: (token: string, range: RevenueSeriesRange, eventId?: string) =>
    request<AdminRevenueSeriesPointDto[]>(
      `/api/superadmin/revenue-series?range=${range}${eventId ? `&eventId=${encodeURIComponent(eventId)}` : ''}`,
      { headers: auth(token) },
    ),
  listEvents: (token: string) => request<TenantSummaryDto[]>('/api/superadmin/events', { headers: auth(token) }),
  createEvent: (token: string, input: CreateEventInput) =>
    sendJson<CreateEventResultDto>('POST', '/api/superadmin/events', input, auth(token)),
  saveEventAsTemplate: (token: string, eventId: string, name: string) =>
    sendJson<EventTemplateDto>('POST', `/api/superadmin/events/${encodeURIComponent(eventId)}/save-as-template`, { name }, auth(token)),
  listTemplates: (token: string) =>
    request<EventTemplateDto[]>('/api/superadmin/templates', { headers: auth(token) }),
  deleteTemplate: (token: string, id: string) =>
    sendJson<{ ok: boolean }>('DELETE', `/api/superadmin/templates/${encodeURIComponent(id)}`, {}, auth(token)),
  exportEventOrders: async (token: string, eventId: string): Promise<void> => {
    const res = await fetch(`/api/superadmin/events/${encodeURIComponent(eventId)}/orders.csv`, { headers: auth(token) });
    if (!res.ok) throw new ApiError(res.status, 'Failed to download CSV');
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    const filename = match?.[1] ?? `jamtick-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  resetEventTransactions: (token: string, eventId: string) =>
    sendJson<{ ok: boolean; deletedOrders: number; releasedOfflineTickets: number }>('POST', `/api/superadmin/events/${encodeURIComponent(eventId)}/reset`, {}, auth(token)),
  setEventStatus: (token: string, eventId: string, status: 'active' | 'paused') =>
    sendJson<{ ok: boolean; status: string }>('PATCH', `/api/superadmin/events/${encodeURIComponent(eventId)}/status`, { status }, auth(token)),
  setEventSalesMode: (token: string, eventId: string, salesMode: EventSalesMode) =>
    sendJson<{ ok: boolean; event: { id: string; salesMode: EventSalesMode } }>(
      'PATCH',
      `/api/superadmin/events/${encodeURIComponent(eventId)}/sales-mode`,
      { salesMode },
      auth(token),
    ),
  updateEventPortalCms: (
    token: string,
    eventId: string,
    patch: { portalVisible?: boolean; portalTeaser?: boolean; carouselVisible?: boolean; sortOrder?: number },
  ) =>
    sendJson<{ ok: boolean; event: Partial<TenantSummaryDto> }>(
      'PATCH',
      `/api/superadmin/events/${encodeURIComponent(eventId)}/portal-cms`,
      patch,
      auth(token),
    ),
  updateEventDeliveryPolicy: (
    token: string,
    eventId: string,
    patch: { endsAt?: string | null; timezone?: string | null; transactionReferencePrefix?: string; allowMixedReferenceFormats?: true },
  ) =>
    sendJson<{ ok: boolean; event: Partial<TenantSummaryDto> }>(
      'PATCH',
      `/api/superadmin/events/${encodeURIComponent(eventId)}/delivery-policy`,
      patch,
      auth(token),
    ),
  // TICK-051 — per-event Thunder/EasySlip API keys.
  getEventVendorKeys: (token: string, eventId: string) =>
    request<EventVendorKeysDto>(`/api/superadmin/events/${encodeURIComponent(eventId)}/vendor-keys`, { headers: auth(token) }),
  updateEventVendorKeys: (token: string, eventId: string, patch: EventVendorKeysPatchDto) =>
    sendJson<{ ok: boolean; vendorKeys: EventVendorKeysDto; checks: Partial<Record<'easyslip' | 'thunder', { status: string; rawMessage?: string }>> }>(
      'POST', `/api/superadmin/events/${encodeURIComponent(eventId)}/vendor-keys`, patch, auth(token),
    ),
  // POST so the id can stay in the URL while the response body — the live key — never does the
  // same round trip as a query string would.
  revealEventVendorKey: (token: string, eventId: string, vendor: 'easyslip' | 'thunder') =>
    sendJson<{ key: string | null }>(
      'POST', `/api/superadmin/events/${encodeURIComponent(eventId)}/vendor-keys/${vendor}/reveal`, {}, auth(token),
    ),
  deleteEvent: (token: string, eventId: string) =>
    sendJson<{ ok: boolean }>('DELETE', `/api/superadmin/events/${encodeURIComponent(eventId)}`, {}, auth(token)),
  listTokens: (token: string, eventId?: string) =>
    request<AccessTokenDto[]>(`/api/superadmin/tokens${eventId ? `?eventId=${encodeURIComponent(eventId)}` : ''}`, { headers: auth(token) }),
  createToken: (token: string, input: CreateTokenInput) =>
    sendJson<CreateTokenResultDto>('POST', '/api/superadmin/tokens', input, auth(token)),
  revokeToken: (token: string, id: string) =>
    sendJson<{ ok: boolean }>('POST', `/api/superadmin/tokens/${encodeURIComponent(id)}/revoke`, {}, auth(token)),
  listAudit: (token: string, filter?: { eventId?: string; action?: string }) => {
    const params = new URLSearchParams();
    if (filter?.eventId) params.set('eventId', filter.eventId);
    if (filter?.action) params.set('action', filter.action);
    const qs = params.toString();
    return request<AuditRowDto[]>(`/api/superadmin/audit${qs ? `?${qs}` : ''}`, { headers: auth(token) });
  },
  listAuditActions: (token: string) => request<string[]>('/api/superadmin/audit/actions', { headers: auth(token) }),
  getSystem: (token: string) => request<ServiceStatusDto[]>('/api/superadmin/system', { headers: auth(token) }),
  listSlipVerifications: (token: string) =>
    request<SlipVerificationAuditDto[]>('/api/superadmin/slip-verifications', { headers: auth(token) }),
  listNotificationDeliveries: (token: string) =>
    request<NotificationDeliveryDto[]>('/api/superadmin/notification-deliveries', { headers: auth(token) }),
  listVendorExchanges: (token: string) =>
    request<{ capture: VendorCaptureSettingsDto; items: VendorExchangeDto[] }>('/api/superadmin/vendor-exchanges', { headers: auth(token) }),
  // POST so the id never lands in a URL: the response can carry personal data in `full` mode.
  revealVendorExchange: (token: string, id: string) =>
    sendJson<{ body: string | null; captureMode: 'redacted' | 'full'; bodyTruncated: boolean }>(
      'POST', `/api/superadmin/vendor-exchanges/${encodeURIComponent(id)}/reveal`, {}, auth(token),
    ),
  setVendorCapture: (token: string, mode: VendorCaptureSettingsDto['mode']) =>
    sendJson<{ ok: boolean; capture: VendorCaptureSettingsDto }>('POST', '/api/superadmin/vendor-capture', { mode }, auth(token)),
  listOperations: (
    token: string,
    filter: { q?: string; eventId?: string; status?: string; limit?: number; offset?: number } = {},
  ) => {
    return sendJson<SuperadminOperationsPageDto>('POST', '/api/superadmin/operations/query', filter, auth(token));
  },
  revealTicketLink: (token: string, linkId: string) =>
    sendJson<{ url: string }>('POST', `/api/superadmin/ticket-links/${encodeURIComponent(linkId)}/reveal`, {}, auth(token)),
  listLeads: (token: string) => request<OrganizerLeadDto[]>('/api/superadmin/leads', { headers: auth(token) }),
  listConcertCalendar: (token: string) => request<ConcertCalendarDto[]>('/api/superadmin/concert-calendar', { headers: auth(token) }),
  createConcertCalendarEntry: (token: string, entry: Omit<ConcertCalendarDto, 'id' | 'createdAt'>) =>
    sendJson<{ ok: boolean; entry: ConcertCalendarDto }>('POST', '/api/superadmin/concert-calendar', entry, auth(token)),
  deleteConcertCalendarEntry: (token: string, id: string) =>
    sendJson<{ ok: boolean }>('DELETE', `/api/superadmin/concert-calendar/${encodeURIComponent(id)}`, {}, auth(token)),
  importConcertFromUrl: (token: string, url: string) =>
    sendJson<{ ok: boolean; draft: ConcertImportDraftDto }>('POST', '/api/superadmin/concert-calendar/import', { url }, auth(token)),
  importConcertCalendarFile: async (token: string, file: File): Promise<ConcertCalendarImportResultDto> => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch('/api/superadmin/concert-calendar/import-file', { method: 'POST', headers: auth(token), body: form });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw new ApiError(res.status, body?.error ?? 'Failed to import file');
    return body as ConcertCalendarImportResultDto;
  },
  updatePortalSettings: (token: string, settings: Partial<PortalSettingsDto>) =>
    sendJson<{ ok: boolean; settings: PortalSettingsDto }>('POST', '/api/superadmin/portal-settings', settings, auth(token)),
  toggleProvider: (token: string, id: ProviderId, enabled: boolean) =>
    sendJson<{ ok: boolean; providers: Record<ProviderId, boolean> }>(
      'POST', `/api/superadmin/providers/${encodeURIComponent(id)}/toggle`, { enabled }, auth(token),
    ),
  updateAdminPolling: (token: string, settings: AdminPollingSettingsDto) =>
    sendJson<{ ok: boolean; adminPolling: AdminPollingSettingsDto }>(
      'POST', '/api/superadmin/admin-polling', settings, auth(token),
    ),
};

export const fmt = (n: number) => '฿' + n.toLocaleString('en-US');

/** "รับ Wristband ไปแล้ว 23 ก.ค. 2569, 16:53 น." — shared wording for the wristband-desk already-checked-in state. */
export function wristbandReceivedLabel(checkedInAt: string): string {
  const d = new Date(checkedInAt);
  const date = d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
  return `รับ Wristband ไปแล้ว ${date}, ${time} น.`;
}

/** e.g. "license_plate: กข 1111" for a single ticket, or "#1 license_plate: กข 1111 · #2 license_plate: กข 2222" for several units. */
export function formatCustomFieldValues(sets: Record<string, string>[]): string {
  return sets
    .map((set, i) => {
      const text = Object.entries(set).map(([k, v]) => `${k}: ${v}`).join(', ');
      return text ? (sets.length > 1 ? `#${i + 1} ${text}` : text) : null;
    })
    .filter(Boolean)
    .join(' · ');
}

/** Same as formatCustomFieldValues, but resolves each key to its Thai (falling back to English) label. */
export function formatCustomFieldValuesWithLabels(sets: Record<string, string>[] | undefined, fields: TicketCustomFieldDto[]): string {
  if (!sets) return '';
  const labelFor = (key: string) => fields.find((f) => f.key === key)?.labelTh || fields.find((f) => f.key === key)?.labelEn || key;
  return sets
    .map((set, i) => {
      const text = Object.entries(set).map(([k, v]) => `${labelFor(k)}: ${v}`).join(', ');
      return text ? (sets.length > 1 ? `#${i + 1} ${text}` : text) : null;
    })
    .filter(Boolean)
    .join(' · ');
}

/* ---------------------------------------------------------------------- */
/* TICK-064 — the organizer's LINE OA console                             */
/* ---------------------------------------------------------------------- */

export type LineOaStatusDto =
  | 'draft' | 'verification_pending' | 'active' | 'verification_failed' | 'disabled' | 'credential_invalid';

/**
 * Mirrors `serializeLineOaConnection`. There is no field for a secret and there never will be:
 * `secretsConfigured` is the whole vocabulary the server has for one.
 */
export interface LineOaConnectionDto {
  id: string;
  ownerScope: 'platform' | 'event' | 'organization';
  ownerId: string | null;
  environment: string;
  capability: 'add_friend_only' | 'messaging';
  displayName: string;
  basicId: string | null;
  messagingChannelId: string | null;
  loginChannelId: string | null;
  liffId: string | null;
  addFriendUrl: string | null;
  botUserId: string | null;
  status: LineOaStatusDto;
  missingFields: string[];
  secretsConfigured: { messagingSecret: boolean; messagingAccessToken: boolean; loginSecret: boolean };
  secretSource: 'deployment' | 'database';
  lastVerifiedAt: string | null;
  lastVerificationError: string | null;
  configVersion: number;
  updatedBy: string;
  updatedAt: string;
}

export interface LineOaCheckDto {
  id: 'identifiers' | 'credential' | 'consistency' | 'webhook' | 'login_channel';
  status: 'passed' | 'failed' | 'skipped' | 'unverified';
  detail: string | null;
}

export interface LineOaVerifyDto {
  connection: LineOaConnectionDto;
  report: { passed: boolean; checks: LineOaCheckDto[]; botUserId: string | null };
}

export interface LineOaSettingsDto {
  organizerConnectionId: string | null;
  addFriendEnabled: boolean;
  message: string;
}

export interface LineOaPreviewDto {
  visible: boolean;
  addFriendUrl: string | null;
  message: string;
  source: 'settings' | 'event_column' | 'none';
}

export interface EventLineOaDto {
  environment: string;
  /** One boolean is all an event administrator is told about JamTick's own OA. */
  platform: { ready: boolean };
  connection: LineOaConnectionDto | null;
  settings: LineOaSettingsDto;
  preview: LineOaPreviewDto;
}

export interface LineOaAuditEntryDto {
  id: string;
  actor: string;
  action: string;
  detail: string;
  createdAt: string;
}

export interface LineOaConnectionInput {
  displayName: string;
  basicId?: string | null;
  messagingChannelId?: string | null;
  liffId?: string | null;
  addFriendUrl?: string | null;
  capability?: 'add_friend_only' | 'messaging';
}

const lineOaBase = (eventSlug?: string) => `${base(eventSlug)}/admin/line-oa`;

export const lineOaApi = {
  get: (token: string, eventSlug?: string) =>
    request<EventLineOaDto>(lineOaBase(eventSlug), { headers: auth(token) }),
  create: (token: string, input: LineOaConnectionInput, eventSlug?: string) =>
    postJson<LineOaConnectionDto>(`${lineOaBase(eventSlug)}/connection`, input, auth(token)),
  update: (token: string, input: Partial<LineOaConnectionInput>, eventSlug?: string) =>
    sendJson<LineOaConnectionDto>('PATCH', `${lineOaBase(eventSlug)}/connection`, input, auth(token)),
  /** Rotation only — the server has no endpoint that returns a secret, by design. */
  rotateSecrets: (
    token: string,
    secrets: { messagingChannelSecret?: string; messagingAccessToken?: string },
    eventSlug?: string,
  ) => postJson<LineOaConnectionDto>(`${lineOaBase(eventSlug)}/connection/secrets`, secrets, auth(token)),
  verify: (token: string, eventSlug?: string) =>
    postJson<LineOaVerifyDto>(`${lineOaBase(eventSlug)}/connection/verify`, {}, auth(token)),
  setEnabled: (token: string, enabled: boolean, eventSlug?: string) =>
    postJson<LineOaConnectionDto>(`${lineOaBase(eventSlug)}/connection/${enabled ? 'enable' : 'disable'}`, {}, auth(token)),
  updateSettings: (token: string, patch: { addFriendEnabled?: boolean; message?: string }, eventSlug?: string) =>
    sendJson<{ settings: LineOaSettingsDto; preview: LineOaPreviewDto | null }>(
      'PATCH', `${lineOaBase(eventSlug)}/settings`, patch, auth(token),
    ),
  audit: (token: string, eventSlug?: string) =>
    request<LineOaAuditEntryDto[]>(`${lineOaBase(eventSlug)}/audit`, { headers: auth(token) }),
};

/**
 * The platform console's view of every LINE OA — JamTick's own and each organizer's.
 *
 * A separate object from `superadminApi` only because it arrived with its own router; the
 * credential and the base path are the same.
 */
export const superadminLineOaApi = {
  environment: (token: string) =>
    request<{ environment: string }>('/api/superadmin/line-oa/environment', { headers: auth(token) }),
  list: (token: string) =>
    request<LineOaConnectionDto[]>('/api/superadmin/line-oa/connections', { headers: auth(token) }),
  create: (
    token: string,
    input: LineOaConnectionInput & {
      ownerScope: 'platform' | 'event' | 'organization';
      ownerId: string | null;
      loginChannelId?: string | null;
    },
  ) => postJson<LineOaConnectionDto>('/api/superadmin/line-oa/connections', input, auth(token)),
  update: (
    token: string,
    id: string,
    input: Partial<LineOaConnectionInput & { loginChannelId: string | null }>,
  ) => sendJson<LineOaConnectionDto>('PATCH', `/api/superadmin/line-oa/connections/${id}`, input, auth(token)),
  /** Rotation only — there is no endpoint that returns a secret. */
  rotateSecrets: (
    token: string,
    id: string,
    secrets: { messagingChannelSecret?: string; messagingAccessToken?: string; loginChannelSecret?: string },
  ) => postJson<LineOaConnectionDto>(`/api/superadmin/line-oa/connections/${id}/secrets`, secrets, auth(token)),
  verify: (token: string, id: string) =>
    postJson<LineOaVerifyDto>(`/api/superadmin/line-oa/connections/${id}/verify`, {}, auth(token)),
  setEnabled: (token: string, id: string, enabled: boolean) =>
    postJson<LineOaConnectionDto>(`/api/superadmin/line-oa/connections/${id}/${enabled ? 'enable' : 'disable'}`, {}, auth(token)),
  audit: (token: string, id: string) =>
    request<LineOaAuditEntryDto[]>(`/api/superadmin/line-oa/connections/${id}/audit`, { headers: auth(token) }),
};

export type LineRolloutMode = 'off' | 'canary' | 'all';

export interface DeliveryRolloutSettingsDto {
  deliverySelectionV2Enabled: boolean;
  lineDeliveryEnabled: boolean;
  lineRolloutMode: LineRolloutMode;
  lineCanaryEventIds: string[];
  organizerLineOverrides: Record<string, boolean>;
  /** TICK-093 — whether `/myticket` answers at all. Platform-wide; off by default. */
  myTicketPortalEnabled: boolean;
}

export interface DeliveryRolloutStatusDto {
  deployment: { deliverySelectionV2: boolean; lineDelivery: boolean };
  runtime: DeliveryRolloutSettingsDto;
  effective: {
    deliverySelectionV2: boolean; lineDelivery: boolean; lineBlockedBy: string | null;
    /** TICK-093 — whether `/myticket` answers. One layer, so it equals the runtime setting. */
    myTicketPortal: boolean;
  };
}

export const superadminRolloutApi = {
  get: (token: string) => request<DeliveryRolloutStatusDto>('/api/superadmin/rollout', { headers: auth(token) }),
  patch: (token: string, patch: Partial<DeliveryRolloutSettingsDto>) =>
    sendJson<{ runtime: DeliveryRolloutSettingsDto; effective: DeliveryRolloutStatusDto['effective'] }>(
      'PATCH', '/api/superadmin/rollout', patch, auth(token),
    ),
  setEventLineOverride: (token: string, eventId: string, enabled: boolean | null) =>
    sendJson<{ runtime: DeliveryRolloutSettingsDto; effective: DeliveryRolloutStatusDto['effective'] }>(
      'PUT', `/api/superadmin/rollout/events/${encodeURIComponent(eventId)}/line`, { enabled }, auth(token),
    ),
};

/* ==================================================================== */
/* TICK-093 — /myticket                                                  */
/* ==================================================================== */

/** What the current browser is allowed to list. `none` is the ordinary first visit, not an error. */
export type MyTicketScope = 'none' | 'device' | 'phone';

export interface MyTicketListItemDto {
  code: string;
  eventSlug: string;
  eventName: string;
  eventEndsAt: string | null;
  qty: number;
  entries: number;
  total: number;
  checkedInAt: string | null;
  purchasedAt: string;
  past: boolean;
}

export interface MyTicketSessionDto {
  scope: MyTicketScope;
  ticketCount: number;
  /** When the phone-wide scope lapses back to device-only. Null outside `phone` scope. */
  rememberedUntil: string | null;
  /**
   * This browser has bought for more than one person, so the cookie alone no longer says whose
   * tickets to show. `scope` is `none` and the viewer has to prove a phone number.
   */
  deviceShared: boolean;
}

export interface MyTicketListDto {
  scope: MyTicketScope;
  orders: MyTicketListItemDto[];
}

export interface MyTicketDetailDto {
  scope: MyTicketScope;
  /** False once the event is more than 30 days past — the purchase stays, the QR does not. */
  qrVisible: boolean;
  order: OrderDto;
  event: { slug: string; name: string; endsAt: string | null };
}

export interface MyTicketOtpRequestDto {
  ok: boolean;
  ttlMinutes: number;
  resendAfterSeconds: number;
  maxAttempts: number;
}

export type MyTicketVerifyResult =
  | { ok: true; scope: MyTicketScope; remembered: boolean; phoneMasked: string; orders: MyTicketListItemDto[] }
  | { ok: false; error: string; attemptsLeft: number };

export const myTicketApi = {
  session: () => request<MyTicketSessionDto>('/api/myticket/session'),
  list: () => request<MyTicketListDto>('/api/myticket/orders'),
  detail: (code: string) => request<MyTicketDetailDto>(`/api/myticket/orders/${encodeURIComponent(code)}`),
  requestOtp: (phone: string) => postJson<MyTicketOtpRequestDto>('/api/myticket/otp/request', { phone }),
  /**
   * Reads the body on failure instead of throwing, because "wrong code, 4 tries left" is the screen
   * the customer needs and an `ApiError` carrying only a message would lose the count.
   */
  verifyOtp: async (phone: string, otp: string, remember: boolean): Promise<MyTicketVerifyResult> => {
    const res = await fetch('/api/myticket/otp/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, otp, remember }),
    });
    const body = await res.json().catch(() => null);
    if (res.ok) return { ok: true, ...(body as Omit<Extract<MyTicketVerifyResult, { ok: true }>, 'ok'>) };
    return {
      ok: false,
      error: body?.error ?? 'ยืนยันรหัสไม่สำเร็จ',
      attemptsLeft: typeof body?.attemptsLeft === 'number' ? body.attemptsLeft : 0,
    };
  },
  logout: () => postJson<{ ok: boolean }>('/api/myticket/logout', {}),
};
